import { DISPLAY, displaySize } from "../../constants.js";
import { layerImageAt, layerZAt } from "./layerHelpers.js";
import { fitBlockFontSize, fitInlineFontSize, heuristicMeasureText, ctaBorderRadius } from "./textFit.js";


/**
 * THE shared banner runtime. (BannerDoc, sizeId) → markup + CSS, so the
 * Composer's live preview and the eventual export are pixel-identical by
 * construction, same as the original.
 *
 * Advanced (layer) mode is fully ported, including sequences (rotators -
 * multiple frames cycling in one layer). Locked snapshots ARE handled here
 * too.
 *
 * Rules honored here, ported unchanged:
 *  - CSS keyframes only, no JS animation
 *  - every preset runs once and HOLDS a complete end frame (fill-mode: forwards)
 *  - prefers-reduced-motion: elements render fully visible, no motion
 *  - 1px rgba(0,0,0,.15) border (ad-distinction convention)
 *  - no external requests: all asset URLs are provided by the caller
 */

export function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Renders {text, colorRanges} as HTML - the shared "data -> markup"
 * direction of the rich-text transform. Used both for the actual banner
 * (headline/subline only; CTA stays plain, per-word color was never
 * scoped there) and for building the rich-text dialog's own initial
 * contentEditable content, so the editor's structure and the real
 * banner's structure can never diverge - same function, not two that
 * happen to agree.
 *
 * colorRanges: [{start, end, color, glow}] - character offsets into
 * `text` (start inclusive, end exclusive), counting \n as one character
 * each. This is the older of two rich-text formats kept for rendering
 * (see wordsToHtml/colorRangesToWords below for the newer, word-based
 * one) - still read here for backward compatibility with any
 * layer/slot that was saved before the word-based editor existed and
 * hasn't been re-edited since. glow is optional - when true, adds a
 * soft text-shadow halo in the same color, no separate color needed.
 */
export function richTextToHtml(text, colorRanges) {
  const safeText = text ?? "";
  const ranges = (colorRanges ?? []).filter(
    (r) => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start
  );

  if (ranges.length === 0) {
    return safeText.split("\n").map(escapeHtml).join("<br>");
  }

  // Sorted, unique breakpoints from every range's start/end, plus the
  // string's own bounds - splits the text into runs that are each
  // either fully inside one range or fully outside all of them.
  const breakpoints = [0, safeText.length];
  for (const r of ranges) {
    breakpoints.push(Math.max(0, Math.min(r.start, safeText.length)));
    breakpoints.push(Math.max(0, Math.min(r.end, safeText.length)));
  }
  const sorted = [...new Set(breakpoints)].sort((a, b) => a - b);

  const segments = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (a === b) continue;
    const covering = ranges.find((r) => r.start <= a && r.end >= b);
    segments.push({
      text: safeText.slice(a, b),
      color: covering ? safeColor(covering.color, null) : null,
      glow: covering?.glow === true,
    });
  }

  return segments
    .map((seg) => {
      const joined = seg.text.split("\n").map(escapeHtml).join("<br>");
      if (!seg.color) return joined;
      const style = seg.glow ? `color:${seg.color};text-shadow:0 0 0.15em ${seg.color}` : `color:${seg.color}`;
      return `<span style="${style}">${joined}</span>`;
    })
    .join("");
}

/** Only allow #rrggbb / #rgb through to the stylesheet. */
function safeColor(value, fallback) {
  return /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(value) ? value : fallback;
}

/**
 * Renders a `words` array as HTML - the newer, structured alternative to
 * richTextToHtml's character-offset colorRanges. Each entry is a fully
 * independent unit ({text, color, glow, bold, breakAfter}), so there's
 * no overlap/breakpoint math needed at all - deliberately much simpler
 * than richTextToHtml, and immune to the entire category of range-overlap
 * edge cases that model had to handle.
 *
 * Words are joined with a single space unless breakAfter is true, in
 * which case a <br> is used instead (and no space). glow reuses the
 * word's own color for its text-shadow, same convention as
 * richTextToHtml - no separate glow-color concept exists.
 *
 * bold's actual weight is computed relative to baseWeight (the
 * containing layer/slot's own font-weight), not a fixed value - a word
 * bolded inside an already-bold (700+) layer would otherwise render at
 * the same weight as its surroundings and produce no visible
 * difference at all, which is the whole point of the toggle. Escalates
 * to 900 in that case; a lighter (400-600) baseline just needs 700.
 */
export function wordsToHtml(words, baseWeight = 400) {
  if (!words?.length) return "";
  const boldWeight = baseWeight >= 700 ? 900 : 700;
  return words
    .map((w, i) => {
      const safeWordText = escapeHtml(w.text ?? "");
      const color = w.color ? safeColor(w.color, null) : null;
      const styles = [];
      if (color) styles.push(`color:${color}`);
      if (color && w.glow) styles.push(`text-shadow:0 0 0.15em ${color}`);
      if (w.bold) styles.push(`font-weight:${boldWeight}`);
      const inner = styles.length ? `<span style="${styles.join(";")}">${safeWordText}</span>` : safeWordText;
      const isLast = i === words.length - 1;
      const sep = isLast ? "" : w.breakAfter ? "<br>" : " ";
      return inner + sep;
    })
    .join("");
}

/**
 * Converts old-format {text, colorRanges} into the new `words` array
 * shape - used once, at editor-open time, only for a layer/slot that
 * doesn't have its own `words` yet (i.e. never edited through the new
 * word-based UI). Lossy in one specific, unavoidable way: colorRanges
 * could color part of a word; words can only color a whole word, so any
 * overlap - even one character - colors the entire word. This is a
 * one-time, in-memory migration for editing purposes only; it doesn't
 * touch or require changing the underlying saved data unless the person
 * actually saves through the new UI.
 */
export function colorRangesToWords(text, colorRanges) {
  const safeText = text ?? "";
  const ranges = (colorRanges ?? []).filter(
    (r) => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start
  );
  const lines = safeText.split("\n");
  const words = [];
  let offset = 0;
  lines.forEach((line, lineIdx) => {
    const lineWords = line.split(/\s+/).filter(Boolean);
    let searchPos = 0;
    lineWords.forEach((wordText, wordIdx) => {
      // Sequential search from the last match's end - handles repeated
      // words in the same line correctly (each occurrence resolves to
      // its own, correct position, not always the first one).
      const localIndex = line.indexOf(wordText, searchPos);
      const start = offset + Math.max(0, localIndex);
      const end = start + wordText.length;
      searchPos = Math.max(0, localIndex) + wordText.length;
      const covering = ranges.find((r) => r.start < end && r.end > start);
      words.push({
        text: wordText,
        color: covering ? safeColor(covering.color, null) : null,
        glow: covering?.glow === true,
        breakAfter: wordIdx === lineWords.length - 1 && lineIdx < lines.length - 1,
      });
    });
    offset += line.length + 1; // +1 for the \n that was split on
  });
  return words;
}

/**
 * Renders rich text for one field, choosing the newer `words` array when
 * present, falling back to the older, character-offset `colorRanges`
 * otherwise. This is the sole place that decision is made - every
 * render call site (headline/subline/CTA/custom layers/sequence frames)
 * goes through this rather than repeating the same check, so existing,
 * never-re-edited assets (words absent, only colorRanges) keep
 * rendering exactly as before, indefinitely.
 */
function renderRichText({ text, colorRanges, words, baseWeight }) {
  return words?.length ? wordsToHtml(words, baseWeight) : richTextToHtml(text ?? "", colorRanges);
}

function box(slot) {
  return `left:${slot.x}px;top:${slot.y}px;width:${slot.w}px;height:${slot.h}px;`;
}

function textStyles(slot) {
  const size = slot.fontSize ?? 14;
  const weight = slot.fontWeight ?? 400;
  const align = slot.align ?? "left";
  return `font-size:${size}px;font-weight:${weight};text-align:${align};line-height:1.15;`;
}

/** Total animation seconds per preset — clamped by the profile. */
export function presetDuration(preset) {
  const durations = {
    static: 0,
    fade_sequence: 3,
    slide_build: 2.4,
    cta_pulse_finale: 3.6,
  };
  return Math.min(durations[preset], DISPLAY.PROFILE.MAX_ANIMATION_S);
}

function presetCss(preset) {
  switch (preset) {
    case "static":
      return "";
    case "fade_sequence":
      return `
@keyframes adv-fade{from{opacity:0}to{opacity:1}}
.el-headline{opacity:0;animation:adv-fade .8s ease-out 0s 1 forwards}
.el-subline{opacity:0;animation:adv-fade .8s ease-out 1s 1 forwards}
.el-cta{opacity:0;animation:adv-fade .8s ease-out 2s 1 forwards}`;
    case "slide_build":
      return `
@keyframes adv-slide{from{opacity:0;transform:translateX(-16px)}to{opacity:1;transform:translateX(0)}}
@keyframes adv-pop{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}
.el-headline{opacity:0;animation:adv-slide .7s cubic-bezier(0.23,1,0.32,1) 0s 1 forwards}
.el-subline{opacity:0;animation:adv-slide .7s cubic-bezier(0.23,1,0.32,1) .5s 1 forwards}
.el-cta{opacity:0;animation:adv-pop .6s cubic-bezier(0.23,1,0.32,1) 1.4s 1 forwards}`;
    case "cta_pulse_finale":
      return `
@keyframes adv-fade{from{opacity:0}to{opacity:1}}
@keyframes adv-pulse{0%,100%{transform:scale(1)}25%{transform:scale(1.06)}50%{transform:scale(1)}75%{transform:scale(1.06)}}
.el-headline,.el-subline{opacity:0;animation:adv-fade .8s ease-out 0s 1 forwards}
.el-cta{opacity:0;animation:adv-fade .6s ease-out .9s 1 forwards,adv-pulse 2s ease-in-out 1.6s 1}`;
    default:
      return "";
  }
}

function clampSeconds(v) {
  return Math.max(0, Math.min(Number.isFinite(v) ? v : 0, DISPLAY.PROFILE.MAX_ANIMATION_S));
}

function iterationCount(n) {
  return n <= 0 ? "infinite" : String(Math.max(1, Math.min(Math.round(n), DISPLAY.PROFILE.MAX_LOOPS)));
}

function inKeyframes(effect) {
  switch (effect) {
    case "slide-left":
      return "from{opacity:0;transform:translateX(-24px)}to{opacity:1;transform:none}";
    case "slide-right":
      return "from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:none}";
    case "slide-up":
      return "from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}";
    case "slide-down":
      return "from{opacity:0;transform:translateY(-24px)}to{opacity:1;transform:none}";
    case "zoom-in":
      return "from{opacity:0;transform:scale(.88)}to{opacity:1;transform:none}";
    case "zoom-out":
      return "from{opacity:0;transform:scale(1.12)}to{opacity:1;transform:none}";
    case "blur":
      return "from{opacity:0;filter:blur(10px)}to{opacity:1;filter:blur(0)}";
    case "pop":
      return "0%{opacity:0;transform:scale(.8)}70%{opacity:1;transform:scale(1.06)}100%{opacity:1;transform:scale(1)}";
    case "rotate":
      return "from{opacity:0;transform:rotate(-10deg) scale(.9)}to{opacity:1;transform:none}";
    case "flip":
      return "from{opacity:0;transform:perspective(500px) rotateY(75deg)}to{opacity:1;transform:perspective(500px) rotateY(0)}";
    case "reveal":
      return "from{opacity:1;clip-path:inset(0 100% 0 0)}to{opacity:1;clip-path:inset(0 0 0 0)}";
    default:
      return "from{opacity:0}to{opacity:1}";
  }
}

function outKeyframes(effect) {
  switch (effect) {
    case "slide-left":
      return "from{opacity:1;transform:none}to{opacity:0;transform:translateX(-24px)}";
    case "slide-right":
      return "from{opacity:1;transform:none}to{opacity:0;transform:translateX(24px)}";
    case "slide-up":
      return "from{opacity:1;transform:none}to{opacity:0;transform:translateY(-24px)}";
    case "slide-down":
      return "from{opacity:1;transform:none}to{opacity:0;transform:translateY(24px)}";
    case "zoom-in":
      return "from{opacity:1;transform:none}to{opacity:0;transform:scale(1.12)}";
    case "zoom-out":
      return "from{opacity:1;transform:none}to{opacity:0;transform:scale(.88)}";
    case "blur":
      return "from{opacity:1;filter:blur(0)}to{opacity:0;filter:blur(10px)}";
    case "pop":
      return "0%{opacity:1;transform:scale(1)}30%{transform:scale(1.06)}100%{opacity:0;transform:scale(.8)}";
    case "rotate":
      return "from{opacity:1;transform:none}to{opacity:0;transform:rotate(10deg) scale(.9)}";
    case "flip":
      return "from{opacity:1;transform:perspective(500px) rotateY(0)}to{opacity:0;transform:perspective(500px) rotateY(75deg)}";
    case "reveal":
      return "from{opacity:1;clip-path:inset(0 0 0 0)}to{opacity:1;clip-path:inset(0 0 0 100%)}";
    default:
      return "from{opacity:1}to{opacity:0}";
  }
}

function emphasisKeyframes(effect) {
  switch (effect) {
    case "shake":
      return "0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}";
    case "wiggle":
      return "0%,100%{transform:rotate(0)}25%{transform:rotate(-5deg)}75%{transform:rotate(5deg)}";
    case "flash":
      return "0%,100%{opacity:1}50%{opacity:.3}";
    case "bounce":
      return "0%,100%{transform:translateY(0)}40%{transform:translateY(-10px)}60%{transform:translateY(-5px)}";
    case "tada":
      return "0%{transform:scale(1)}10%,20%{transform:scale(.95) rotate(-3deg)}30%,50%,70%,90%{transform:scale(1.05) rotate(3deg)}40%,60%,80%{transform:scale(1.05) rotate(-3deg)}100%{transform:scale(1)}";
    case "glow":
      // box-shadow pulse instead of a scale transform - only the thin
      // region around the element's edges changes each frame, not its
      // entire interior, while still reading as "pulsing" visually.
      return "0%,100%{box-shadow:0 0 0 rgba(255,255,255,0)}50%{box-shadow:0 0 40px 10px rgba(255,255,255,.6)}";
    default:
      return "0%,100%{transform:scale(1)}50%{transform:scale(1.08)}";
  }
}

function poseHidden(effect) {
  switch (effect) {
    case "slide-left":
      return "opacity:0;transform:translateX(-24px)";
    case "slide-right":
      return "opacity:0;transform:translateX(24px)";
    case "slide-up":
      return "opacity:0;transform:translateY(24px)";
    case "slide-down":
      return "opacity:0;transform:translateY(-24px)";
    case "zoom-in":
      return "opacity:0;transform:scale(.88)";
    case "zoom-out":
      return "opacity:0;transform:scale(1.12)";
    case "pop":
      return "opacity:0;transform:scale(.8)";
    case "rotate":
      return "opacity:0;transform:rotate(-10deg) scale(.9)";
    case "flip":
      return "opacity:0;transform:perspective(500px) rotateY(75deg)";
    default:
      return "opacity:0;transform:none";
  }
}

function framePeak(effect) {
  switch (effect) {
    case "shake":
      return "opacity:1;transform:translateX(6px)";
    case "wiggle":
      return "opacity:1;transform:rotate(4deg)";
    case "bounce":
      return "opacity:1;transform:translateY(-8px)";
    case "flash":
      return "opacity:.35;transform:none";
    case "tada":
      return "opacity:1;transform:scale(1.06) rotate(3deg)";
    case "glow":
      return "opacity:1;box-shadow:0 0 40px 10px rgba(255,255,255,.6)";
    default:
      return "opacity:1;transform:scale(1.08)";
  }
}

function sequenceFrameBody(frame, cycle, isLast) {
  const C = Math.max(0.1, cycle);
  const pct = (t) => Math.max(0, Math.min(100, Math.round((t / C) * 10000) / 100));
  const NEUTRAL = "opacity:1;transform:none";
  const inHide = poseHidden(frame.in.effect);
  const stops = [[0, inHide]];
  if (frame.in.delay > 0.001) stops.push([pct(frame.in.delay), inHide]);
  const holdStart = pct(frame.in.delay + frame.in.duration);
  stops.push([holdStart, NEUTRAL]);
  const holdEnd = frame.out && !isLast ? pct(frame.out.start) : 100;
  if (frame.emphasis && holdEnd > holdStart) {
    const beats = Math.max(1, Math.min(Math.round(frame.emphasis.times) || 1, 6));
    const peak = framePeak(frame.emphasis.effect);
    const span = holdEnd - holdStart;
    for (let i = 0; i < beats; i++) {
      const mid = holdStart + (span * (i + 0.5)) / beats;
      const rest = holdStart + (span * (i + 1)) / beats;
      stops.push([Math.round(mid * 100) / 100, peak]);
      if (rest < holdEnd) stops.push([Math.round(rest * 100) / 100, NEUTRAL]);
    }
    stops.push([holdEnd, NEUTRAL]);
  }
  if (frame.out && !isLast) {
    const outHide = poseHidden(frame.out.effect);
    stops.push([pct(frame.out.start), NEUTRAL]);
    stops.push([pct(frame.out.start + frame.out.duration), outHide]);
    stops.push([100, outHide]);
  } else {
    stops.push([100, NEUTRAL]);
  }
  const byPct = new Map();
  for (const [p, s] of stops) byPct.set(p, s);
  return [...byPct.keys()]
    .sort((a, b) => a - b)
    .map((k) => `${k}%{${byPct.get(k)}}`)
    .join("");
}

function layerAnimationCss(id, anim) {
  const keyframes = [];
  const parts = [];
  let baseHidden = false;
  if (anim.in) {
    keyframes.push(`@keyframes kin-${id}{${inKeyframes(anim.in.effect)}}`);
    parts.push(`kin-${id} ${clampSeconds(anim.in.duration)}s ease-out ${clampSeconds(anim.in.delay)}s 1 forwards`);
    baseHidden = true;
  }
  if (anim.emphasis) {
    const iter = iterationCount(anim.emphasis.times);
    const fill = iter === "infinite" ? "" : " forwards";
    keyframes.push(`@keyframes kemp-${id}{${emphasisKeyframes(anim.emphasis.effect)}}`);
    parts.push(
      `kemp-${id} ${clampSeconds(anim.emphasis.duration)}s ease-in-out ${clampSeconds(anim.emphasis.delay)}s ${iter}${fill}`
    );
  }
  if (anim.out) {
    keyframes.push(`@keyframes kout-${id}{${outKeyframes(anim.out.effect)}}`);
    parts.push(`kout-${id} ${clampSeconds(anim.out.duration)}s ease-in ${clampSeconds(anim.out.start)}s 1 forwards`);
  }
  if (parts.length === 0) return { keyframes: "", rule: "" };
  return {
    keyframes: keyframes.join("\n"),
    rule: `.lyr-${id}{${baseHidden ? "opacity:0;" : ""}animation:${parts.join(",")}}`,
  };
}

/**
 * The rendered font size for one text layer at one size. Auto-fit (the
 * default - fontSizeAuto undefined or true) finds the best-fitting size
 * for this specific box via the real measure-and-fit algorithm; a manual
 * override (fontSizeAuto === false) uses exactly what the user typed, no
 * scaling at all. ctx.measureText is real Canvas measurement in the
 * browser; Node has no Canvas, so it falls back to the same character-width
 * heuristic gif.js's own text fitting already uses.
 */
export function resolveFontSize(layer, text, boxW, boxH, sizeId, ctx) {
  const frozen = layer.fontSizeBySize?.[sizeId];
  if (Number.isFinite(frozen)) return frozen;
  const stored = layer.fontSize ?? 14;
  if (layer.fontSizeAuto === false) return stored;
  // Only the GIF export path sets this (see gif.js) - Node has no real
  // Canvas, so rather than guess via the character-width heuristic here,
  // this defers to a real Canvas measurement pass Puppeteer runs after
  // the page loads (see capture.js). null signals "not yet resolved" to
  // the caller, which emits data-fit-* attributes instead of a font-size.
  if (ctx.deferFontFit) return null;
  const measureText = ctx.measureText
    ? (t, fs) => ctx.measureText(t, fs, layer.fontWeight ?? 400)
    : heuristicMeasureText;
  return layer.role === "cta"
    ? fitInlineFontSize(text, boxW, boxH, { measureText })
    : fitBlockFontSize(text, boxW, boxH, { measureText });
}

/**
 * Turns a resolveFontSize result into the two HTML fragments its callers
 * need: a font-size style declaration when resolved, or data-fit-*
 * attributes for capture.js's in-page fitting pass to pick up later when
 * deferred (fontSize === null). Centralized here so both renderLayers
 * call sites (regular layer text, sequence frame text) share the exact
 * same fragment-building logic rather than duplicating it.
 */
function fontSizeFragments(fontSize, text, boxW, boxH, role, weight) {
  if (fontSize === null) {
    return {
      style: "",
      attrs: ` data-fit-text="${escapeHtml(text)}" data-fit-boxw="${boxW}" data-fit-boxh="${boxH}" data-fit-role="${role}" data-fit-weight="${weight}"`,
    };
  }
  return { style: `font-size:${fontSize}px;`, attrs: "" };
}

function renderLayers(doc, sizeId, layout, ctx) {
  const size = displaySize(sizeId);
  if (!size) throw new Error(`Unknown display size: ${sizeId}`);
  const snap = doc.lockedSnapshots?.[sizeId];
  const bgImage = snap
    ? snap.background
    : (doc.backgroundBySize?.[sizeId] ?? doc.advancedBackground);
  const layers = snap
    ? snap.layers
    : [...(doc.layers ?? [])].sort((a, b) => layerZAt(a, sizeId) - layerZAt(b, sizeId));
  // Matches template-mode's own convention (renderBanner: layout.imageAsBackground
  // ? "#000" : bg) exactly - checks the same layout.imageAsBackground flag, not a
  // re-derived guess from whether an image happens to be selected yet, so a
  // template that's conceptually "image as background" (e.g. Custom Image) stays
  // black even before any image is chosen. Also covers the two advanced-mode-only
  // paths: bgImage (the separate advancedBackground/backgroundBySize mechanism) and
  // a regular "background"-role layer (what layersFromTemplate actually creates
  // when converting a template's own full-bleed image into advanced mode).
  const hasFullBleedImage =
    layout?.imageAsBackground === true ||
    !!bgImage ||
    layers.some((l) => l.type === "image" && l.role === "background");
  const bg = hasFullBleedImage
    ? "#000"
    : safeColor(snap ? snap.bgColor : doc.slots.bgColor, "#ffffff");
  const parts = [];
  const animKeyframes = [];
  const animRules = [];

  if (bgImage && ctx.urlFor) {
    const bgUrl = ctx.urlFor(bgImage);
    if (bgUrl) {
      parts.push(
        `<div class="slot" style="left:0;top:0;width:${size.w}px;height:${size.h}px;background-image:url('${bgUrl}');background-size:cover;background-position:center;background-repeat:no-repeat"></div>`
      );
    }
  }

  for (const layer of layers) {
    const b = layer.boxes[sizeId];
    if (!b || b.hidden) continue;
    const roleClass = `el-${layer.role} lyr-${layer.id}`;

    if (layer.sequence && layer.sequence.frames.length > 0) {
      const seq = layer.sequence;
      const cycle = clampSeconds(seq.cycleDuration) || 0.1;
      const iter = iterationCount(seq.loops);
      const iterFill = iter === "infinite" ? "" : " forwards";
      const color = safeColor(layer.color ?? "#111111", "#111111");
      const fill = layer.bg ? `background:${safeColor(layer.bg, "#111111")};` : "";
      const align = layer.align ?? "left";
      const fit = layer.fit === "contain" ? "contain" : "cover";
      const children = [];
      seq.frames.forEach((frame, i) => {
        const isLast = i === seq.frames.length - 1;
        const lastClass = isLast ? " seqlast" : "";
        animKeyframes.push(`@keyframes kseq-${layer.id}-${i}{${sequenceFrameBody(frame, cycle, isLast)}}`);
        const anim = `animation:kseq-${layer.id}-${i} ${cycle}s linear 0s ${iter}${iterFill}`;
        if (layer.type === "image") {
          const url = ctx.urlFor ? ctx.urlFor(frame.imageSource ?? null) : null;
          const bgImg = url
            ? `background-image:url('${url}');background-size:${fit};background-position:center;background-repeat:no-repeat;`
            : "";
          children.push(`<div class="seqframe${lastClass}" style="${bgImg}${anim}"></div>`);
        } else {
          const fontSize = resolveFontSize(layer, frame.text ?? "", b.w, b.h, sizeId, ctx);
          const fsFrag = fontSizeFragments(fontSize, frame.text ?? "", b.w, b.h, layer.role, layer.fontWeight ?? 400);
          children.push(
            `<div class="seqframe seqtext${lastClass}" style="${fsFrag.style}font-weight:${layer.fontWeight ?? 400};text-align:${align};color:${color};${fill}${layer.role === "cta" && layer.bg ? `border-radius:${ctaBorderRadius(b.h)}px;` : ""}${anim}"${fsFrag.attrs}><span class="rich-text-wrap">${renderRichText({ text: frame.text, colorRanges: frame.colorRanges, words: frame.words, baseWeight: layer.fontWeight ?? 400 })}</span></div>`
          );
        }
      });
      parts.push(`<div class="slot layer ${roleClass}" style="${box(b)}">${children.join("")}</div>`);
      continue;
    }

    if (layer.animation) {
      const css = layerAnimationCss(layer.id, layer.animation);
      if (css.keyframes) animKeyframes.push(css.keyframes);
      if (css.rule) animRules.push(css.rule);
    }
    if (layer.type === "image") {
      const url = ctx.urlFor ? ctx.urlFor(layerImageAt(layer, sizeId)) : null;
      if (!url) continue;
      const fit = layer.fit === "contain" ? "contain" : "cover";
      parts.push(
        `<div class="slot layer ${roleClass}" style="${box(b)}background-image:url('${url}');background-size:${fit};background-position:center;background-repeat:no-repeat"></div>`
      );
    } else {
      const color = safeColor(layer.color ?? "#111111", "#111111");
      const fill = layer.bg ? `background:${safeColor(layer.bg, "#111111")};` : "";
      const align = layer.align ?? "left";
      const fontSize = resolveFontSize(layer, layer.text ?? "", b.w, b.h, sizeId, ctx);
      const fsFrag = fontSizeFragments(fontSize, layer.text ?? "", b.w, b.h, layer.role, layer.fontWeight ?? 400);
      const richText = renderRichText({ text: layer.text, colorRanges: layer.colorRanges, words: layer.words, baseWeight: layer.fontWeight ?? 400 });
      parts.push(
        `<div class="slot layer ${roleClass}" style="${box(b)}${fsFrag.style}font-weight:${layer.fontWeight ?? 400};text-align:${align};color:${color};${fill}${layer.role === "cta" && layer.bg ? `border-radius:${ctaBorderRadius(b.h)}px;` : ""}"${fsFrag.attrs}><span class="rich-text-wrap">${richText}</span></div>`
      );
    }
  }

  if (doc.slots.complianceLine) {
    const cbox = complianceStripBox(size.w, size.h, doc.slots.complianceFontSize);
    parts.push(
      `<div class="slot compliance" style="${box(cbox)}font-size:${cbox.fontSize}px">${escapeHtml(doc.slots.complianceLine)}</div>`
    );
  }

  const fontFamily = ctx.fontFace
    ? `'${ctx.fontFace.family}',system-ui,-apple-system,'Segoe UI',Arial,sans-serif`
    : `system-ui,-apple-system,'Segoe UI',Arial,sans-serif`;

  const css = `
${ctx.fontFace ? `@font-face{font-family:'${ctx.fontFace.family}';src:url('${ctx.fontFace.url}') format('woff2');font-display:swap}` : ""}
*{margin:0;padding:0;box-sizing:border-box}
#ad{position:relative;overflow:hidden;width:${size.w}px;height:${size.h}px;background:${bg};font-family:${fontFamily};cursor:pointer}
#ad::after{content:"";position:absolute;inset:0;border:1px solid rgba(0,0,0,.15);pointer-events:none;z-index:9}
.slot{position:absolute;overflow:hidden}
.layer.el-headline,.layer.el-subline,.layer.el-custom{display:flex;align-items:center;word-break:break-word;line-height:1.15}
.rich-text-wrap{min-width:0}
.layer[style*="center"]{justify-content:center}
.layer.el-cta{display:flex;align-items:center;justify-content:center;font-weight:600;white-space:nowrap}
.compliance{display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.72);color:#fff;letter-spacing:.02em;z-index:8}
.seqframe{position:absolute;inset:0;overflow:hidden;opacity:0}
.seqframe.seqtext{display:flex;align-items:center;word-break:break-word;line-height:1.15}
.seqframe.seqtext[style*="center"]{justify-content:center}
${animKeyframes.join("\n")}
${animRules.join("\n")}
@media (prefers-reduced-motion: reduce){.slot.layer{animation:none !important;opacity:1 !important;transform:none !important;filter:none !important;clip-path:none !important}.seqframe{animation:none !important;opacity:0 !important;transform:none !important}.seqframe.seqlast{opacity:1 !important}}
`.trim();

  return { bodyHtml: `<div id="ad">${parts.join("")}</div>`, width: size.w, height: size.h, css };
}

export function isAdvanced(doc) {
  return doc.advanced === true && Array.isArray(doc.layers);
}

export function hasLayerContent(doc) {
  return Array.isArray(doc.layers) && doc.layers.length > 0;
}

function finiteLoops(n) {
  return n <= 0 ? 1 : Math.max(1, Math.min(Math.round(n), DISPLAY.PROFILE.MAX_LOOPS));
}

export function advancedTimelineDuration(doc) {
  let max = 0;
  for (const layer of doc.layers ?? []) {
    if (layer.sequence) {
      max = Math.max(max, layer.sequence.cycleDuration * finiteLoops(layer.sequence.loops));
      continue;
    }
    const a = layer.animation;
    if (!a) continue;
    if (a.in) max = Math.max(max, a.in.delay + a.in.duration);
    if (a.emphasis) {
      max = Math.max(max, a.emphasis.delay + a.emphasis.duration * finiteLoops(a.emphasis.times));
    }
    if (a.out) max = Math.max(max, a.out.start + a.out.duration);
  }
  return max;
}

export function resolveSlots(doc, sizeId) {
  const override = doc.overrides[sizeId];
  if (!override) return { slots: doc.slots, hidden: [] };
  const { hidden, ...slotPatch } = override;
  return { slots: { ...doc.slots, ...slotPatch }, hidden: hidden ?? [] };
}

export function complianceStripBox(w, h, customFontSize) {
  if (customFontSize && customFontSize > 0) {
    const size = Math.max(5, Math.min(Math.round(customFontSize), Math.floor(h * 0.4)));
    const stripH = Math.min(h, size + (h <= 100 ? 4 : 6));
    return { x: 0, y: h - stripH, w, h: stripH, fontSize: size, align: "center" };
  }
  const stripH = h <= 100 ? 11 : 14;
  return { x: 0, y: h - stripH, w, h: stripH, fontSize: h <= 100 ? 7 : 9, align: "center" };
}

export function renderBanner(doc, sizeId, layout, ctx) {
  const size = displaySize(sizeId);
  if (!size) throw new Error(`Unknown display size: ${sizeId}`);
  if (hasLayerContent(doc)) return renderLayers(doc, sizeId, layout, ctx);
  const { slots, hidden } = resolveSlots(doc, sizeId);

  const bg = safeColor(slots.bgColor, "#ffffff");
  const ctaBg = safeColor(slots.ctaColor, "#111111");
  const text = safeColor(slots.textColor, "#111111");
  const imageUrl = ctx.urlFor ? ctx.urlFor(slots.imageSource) : null;
  const logoUrl = slots.logoRefId && ctx.urlFor ? ctx.urlFor({ kind: "reference", id: slots.logoRefId }) : null;
  const showImage = !hidden.includes("image") && imageUrl !== null;
  const baked = new Set(slots.bakedText ?? []);
  const showHeadline = !baked.has("headline");
  const showSubline =
    !hidden.includes("subline") &&
    layout.subline !== undefined &&
    !!slots.subline &&
    !baked.has("subline");
  const showCta = !baked.has("cta");

  const parts = [];

  if (layout.imageAsBackground && showImage) {
    parts.push(
      `<div class="slot img-bg" style="left:0;top:0;width:${size.w}px;height:${size.h}px;background-image:url('${imageUrl}')"></div>`
    );
    if (layout.scrim) {
      parts.push(
        `<div class="slot scrim" style="left:0;top:0;width:${size.w}px;height:${size.h}px"></div>`
      );
    }
  }
  if (layout.band) {
    parts.push(`<div class="slot band" style="${box(layout.band)}background:${bg}"></div>`);
  }
  if (layout.image && !layout.imageAsBackground && showImage) {
    parts.push(
      `<div class="slot img" style="${box(layout.image)}background-image:url('${imageUrl}')"></div>`
    );
  }
  if (logoUrl) {
    parts.push(
      `<div class="slot logo" style="${box(layout.logo)}background-image:url('${logoUrl}')"></div>`
    );
  }
  if (showHeadline) {
    parts.push(
      `<div class="slot el-headline" style="${box(layout.headline)}${textStyles(layout.headline)}color:${text}"><span class="rich-text-wrap">${renderRichText({ text: slots.headline, colorRanges: slots.headlineColorRanges, words: slots.headlineWords, baseWeight: layout.headline.fontWeight ?? 400 })}</span></div>`
    );
  }
  if (showSubline && layout.subline) {
    parts.push(
      `<div class="slot el-subline" style="${box(layout.subline)}${textStyles(layout.subline)}color:${text};opacity:.92"><span class="rich-text-wrap">${renderRichText({ text: slots.subline, colorRanges: slots.sublineColorRanges, words: slots.sublineWords, baseWeight: layout.subline.fontWeight ?? 400 })}</span></div>`
    );
  }
  if (showCta) {
    parts.push(
      `<div class="slot el-cta" style="${box(layout.cta)}font-size:${layout.cta.fontSize ?? 13}px;background:${ctaBg};border-radius:${ctaBorderRadius(layout.cta.h)}px"><span class="rich-text-wrap">${renderRichText({ text: slots.cta, colorRanges: slots.ctaColorRanges, words: slots.ctaWords, baseWeight: 600 })}</span></div>`
    );
  }
  if (slots.complianceLine) {
    const cbox = slots.complianceFontSize
      ? complianceStripBox(size.w, size.h, slots.complianceFontSize)
      : layout.compliance;
    parts.push(
      `<div class="slot compliance" style="${box(cbox)}font-size:${cbox.fontSize ?? 9}px">${escapeHtml(slots.complianceLine)}</div>`
    );
  }

  const fontFamily = ctx.fontFace
    ? `'${ctx.fontFace.family}',system-ui,-apple-system,'Segoe UI',Arial,sans-serif`
    : `system-ui,-apple-system,'Segoe UI',Arial,sans-serif`;

  const css = `
${ctx.fontFace ? `@font-face{font-family:'${ctx.fontFace.family}';src:url('${ctx.fontFace.url}') format('woff2');font-display:swap}` : ""}
*{margin:0;padding:0;box-sizing:border-box}
#ad{position:relative;overflow:hidden;width:${size.w}px;height:${size.h}px;background:${layout.imageAsBackground ? "#000" : bg};font-family:${fontFamily};cursor:pointer}
#ad::after{content:"";position:absolute;inset:0;border:1px solid rgba(0,0,0,.15);pointer-events:none;z-index:9}
.slot{position:absolute;overflow:hidden}
.img-bg,.img{background-size:cover;background-position:center}
.logo{background-size:contain;background-position:left center;background-repeat:no-repeat}
.scrim{background:linear-gradient(to bottom,rgba(0,0,0,.05) 0%,rgba(0,0,0,.62) 100%)}
.el-headline,.el-subline{display:flex;align-items:center;word-break:break-word}
.rich-text-wrap{min-width:0}
.el-headline[style*="center"],.el-subline[style*="center"]{justify-content:center}
.el-cta{display:flex;align-items:center;justify-content:center;color:#fff;font-weight:600;white-space:nowrap}
.compliance{display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.72);color:#fff;letter-spacing:.02em;z-index:8}
${presetCss(doc.animationPreset)}
@media (prefers-reduced-motion: reduce){.el-headline,.el-subline,.el-cta{animation:none !important;opacity:1 !important;transform:none !important}}
`.trim();

  return {
    bodyHtml: `<div id="ad">${parts.join("")}</div>`,
    width: size.w,
    height: size.h,
    css,
  };
}

export function renderBannerSrcdoc(doc, sizeId, layout, ctx) {
  const rendered = renderBanner(doc, sizeId, layout, ctx);
  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;overflow:hidden}${rendered.css}</style></head><body>${rendered.bodyHtml}</body></html>`;
}